import { FilePurpose } from '@/api/enums';
import { isKeycloakEnabled } from '@/auth';
import { client } from '@/client';
import { File } from '@/client/files';
import { LoadingStatuses } from '@/common/constants';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import classNames from 'classnames';
import { Dialog, DialogProps } from 'primereact/dialog';
import {
  FileUpload,
  FileUploadHeaderTemplateOptions,
  FileUploadUploadEvent,
} from 'primereact/fileupload';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 530px;

  p {
    color: var(--black-main);
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
  }

  .p-fileupload {
    width: 100%;
    border: 1px dashed var(--gray-border);
    border-radius: var(--grand-border-radius);
    position: relative;
    background-color: transparent;

    &:has(.p-fileupload-highlight) {
      background-color: var(--beige-main);
      i {
        color: var(--green-dark);
      }
    }

    &:has(.file-uploaded) {
      border-color: var(--green-dark);
      background-color: var(--green-pale);
      i {
        color: var(--green-dark);
      }
    }
  }

  .p-fileupload-buttonbar {
    border: none;
    font-size: var(--small-font-size);
    font-weight: 500;
    color: var(--dark-gray);
    background: transparent;
  }

  i {
    color: var(--black-main);
    font-size: var(--heading-medium-font-size);
  }

  .p-fileupload-choose {
    background-color: transparent;
    color: var(--black-main);
    border: none;
    font-weight: 600;
    font-size: var(--small-font-size);
    padding: 0px;
    z-index: 100;

    .p-button-icon {
      margin: 0;
    }
  }

  .p-fileupload-content {
    position: absolute;
    top: 0;
    width: 100%;
    border: none;
    background: transparent;
  }

  .p-progressbar {
    padding-inline: var(--default-padding);
    background-color: transparent;

    .p-progressbar-value {
      background-color: var(--gray-darker);
    }
  }

  .p-fileupload-row {
    display: none;
  }
`;

const StyledError = styled.p`
  span {
    color: var(--red-main);
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
  }
  padding-top: var(--xxsmall-padding);
  padding-left: var(--default-padding);
  margin: 0;
  width: 100%;
`;

type MediaUploadModalProps = {
  description: string;
  onFileUpload: (file: File | null) => void;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  setIsSubmitDisabled?: (isSubmitDisabled: boolean) => void;
  purpose: FilePurpose;
  accept: string;
  maxFileSize: number;
} & DialogProps;

export const MediaUploadModal: React.FC<MediaUploadModalProps> = ({
  description,
  onHide,
  selectedFile,
  setSelectedFile,
  setIsSubmitDisabled,
  purpose,
  accept,
  maxFileSize,
  visible,
  onFileUpload,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const ref = React.useRef<FileUpload>(null);

  const isKeycloakAuth = isKeycloakEnabled();

  const [isDragEnter, setIsDragEnter] = useState(false);
  const [isDropped, setIsDropped] = useState(false);
  const [error, setError] = useState<string[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(selectedFile);

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const changeUploadingState = (isUploading: boolean) => {
    setIsUploading(isUploading);
    if (setIsSubmitDisabled) {
      setIsSubmitDisabled(isUploading);
    }
  };

  const onUpload = async (e: FileUploadUploadEvent) => {
    let response;
    try {
      response = JSON.parse(e.xhr.response);
    } catch {
      setError([t('error.fileUploadFailed')]);
      changeUploadingState(false);
      return;
    }
    setUploadedFile(response);

    setError([]);
    changeUploadingState(false);
    setIsDropped(true);
  };

  const headerTemplateEnter = (options: FileUploadHeaderTemplateOptions) => {
    const { className } = options;

    return (
      <FlexContainer className={className}>
        <i className="pi pi-arrow-circle-down" />
        <div>{t('file.upload.drop')}</div>
      </FlexContainer>
    );
  };

  const headerTemplateLeave = (options: FileUploadHeaderTemplateOptions) => {
    const { className, chooseButton } = options;

    return (
      <FlexContainer className={className}>
        <i className="pi pi-arrow-circle-up" />
        <div>
          {t('file.upload')} {chooseButton}
        </div>
      </FlexContainer>
    );
  };

  const headerTemplateDropped = (options: FileUploadHeaderTemplateOptions) => {
    const { className } = options;

    return (
      <FlexContainer className={classNames(className, 'file-uploaded')}>
        <i className="pi pi-arrow-circle-up" />
        <div style={{ textAlign: 'center' }}>
          {t('file.upload.success', { fileName: uploadedFile?.fileName })}
        </div>
      </FlexContainer>
    );
  };

  const onDialogHide = () => {
    onHide();
    setIsDragEnter(false);
    setIsDropped(false);
    setError([]);
    setUploadedFile(null);
  };

  return (
    <StyledDialog
      className="v2"
      blockScroll
      visible={visible}
      header={<h1>{t('generic.upload')}</h1>}
      onHide={onDialogHide}
      draggable={false}
      data-testid="media-upload-modal"
    >
      <FlexContainer justify="flex-start" direction="column">
        <p dangerouslySetInnerHTML={{ __html: description }} />
        <div
          className="w-full"
          translate="no"
          onDragOverCapture={() => {
            setIsDropped(false);
            setIsDragEnter(true);
          }}
          onDragLeaveCapture={() => setIsDragEnter(false)}
          onDropCapture={() => setIsDropped(true)}
        >
          <FileUpload
            ref={ref}
            headerTemplate={
              isDropped && uploadedFile?.id
                ? headerTemplateDropped
                : isDragEnter
                ? headerTemplateEnter
                : headerTemplateLeave
            }
            name="file"
            url={client.files.getFileUploadUrl()}
            accept={accept}
            maxFileSize={maxFileSize}
            onUpload={onUpload}
            auto
            chooseLabel={t('file.upload.browse')}
            chooseOptions={{ icon: 'none' }}
            withCredentials
            onValidationFail={(file) => {
              if (file.size >= maxFileSize) {
                setError((state) => [
                  t('error.fileSize', {
                    mb: maxFileSize / (1000 * 1000),
                  }),
                  ...state,
                ]);
                setIsDragEnter(false);
                setIsDropped(false);
              }
            }}
            onError={(event) => {
              let errorMessage: string | undefined;
              try {
                const response = JSON.parse(event?.xhr?.response);
                errorMessage = response?.message;
              } catch {
                // Response wasn't valid JSON (network error, timeout, etc.)
              }
              if (errorMessage) {
                setError((state) => [errorMessage as string, ...state]);
              } else {
                setError((state) => [t('error.fileUploadFailed'), ...state]);
              }
              changeUploadingState(false);
              setIsDragEnter(false);
              setIsDropped(false);
            }}
            onBeforeSend={(event) => {
              if (user && isKeycloakAuth) {
                event.xhr.setRequestHeader(
                  'Authorization',
                  `Bearer ${user?.access_token}`,
                );
              }
            }}
            onBeforeUpload={(event) => {
              changeUploadingState(true);
              event.formData.append('purpose', purpose);
            }}
          />
        </div>
        {!!error?.length && (
          <StyledError>
            {error.map((error, index) => (
              <span key={index}>
                {error}.<br />
              </span>
            ))}
          </StyledError>
        )}
      </FlexContainer>

      <FlexContainer justify="flex-start" className="mt-4" gap={12}>
        <AppButton
          onClick={() => {
            onFileUpload(uploadedFile);
            setSelectedFile(uploadedFile);
            onDialogHide();
          }}
          label={t('button.saveChanges')}
          state={isUploading ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
          isDisabled={isUploading || !!error.length || !uploadedFile}
        />
        <AppButton
          label={t('button.cancel')}
          severity="secondary"
          type="outlined"
          onClick={onDialogHide}
          className="mr-3"
          isDisabled={isUploading}
        />
      </FlexContainer>
    </StyledDialog>
  );
};
